import TaskSolutionSteps from "../../../../components/TaskSolutionSteps";
import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import EquationInput from '../../../../components/inputs/EquationInput';

/* === Helpers (kanoniczny zapis szkolny) === */
function sortVariablePart(str) {
  return str.replace(/[a-z]{2,}/gi, m => m.split('').sort().join(''));
}
function formatCoeffVar(coeff, vars) {
  if (!vars) return String(coeff);
  if (coeff === 1) return vars;
  if (coeff === -1) return `-${vars}`;
  return `${coeff}${vars}`;
}
function compareTermsCanonical(a, b) {
  const va = a.match(/[a-z]+/i)?.[0] || '';
  const vb = b.match(/[a-z]+/i)?.[0] || '';
  if (va.length !== vb.length) return vb.length - va.length;
  const cmp = va.localeCompare(vb);
  if (cmp !== 0) return cmp;
  return a.localeCompare(b);
}
function normalizeTerm(raw) {
  let s = raw.replace(/\s+/g, '').replace(/\*/g, '');
  let sign = 1;
  if (s.startsWith('-')) { sign = -1; s = s.slice(1); }
  else if (s.startsWith('+')) { s = s.slice(1); }
  const vars = s.match(/[a-z]+/i)?.[0] || '';
  const coeffStr = s.replace(/[a-z]+/gi, '');
  let coeff = coeffStr === '' ? 1 : Number(coeffStr);
  if (Number.isNaN(coeff)) coeff = 1;

  const v = sortVariablePart(vars);
  const c = sign * coeff;

  if (!v) return String(c);
  if (c === 1) return v;
  if (c === -1) return `-${v}`;
  return `${c}${v}`;
}
function normalizeExpression(expr) {
  if (!expr || typeof expr !== 'string') return '';
  const prepared = expr
    .replace(/−/g, '-')
    .replace(/\s+/g, '')
    .replace(/\*/g, '')
    .replace(/-/g, '+-')
    .toLowerCase();

  const terms = prepared.split('+').filter(Boolean).map(normalizeTerm);
  terms.sort(compareTermsCanonical);

  return terms
    .map((t, i) => (i === 0 ? t : (t.startsWith('-') ? t : `+${t}`)))
    .join('')
    .replace(/\s+/g, '');
}

/* === GENERATE TASK === */
export function generateTask() {
  const variables = ['x', 'y', 'z'];
  let var1 = variables[Math.floor(Math.random() * variables.length)];
  let var2 = var1;
  while (var2 === var1) var2 = variables[Math.floor(Math.random() * variables.length)];

  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  const c = Math.floor(Math.random() * 9) + 1;
  const d = Math.floor(Math.random() * 9) + 1;

  const sign1 = Math.random() < 0.5 ? '+' : '-';
  const sign2 = Math.random() < 0.5 ? '+' : '-';

  const bSigned = sign1 === '+' ? b : -b;
  const dSigned = sign2 === '+' ? d : -d;

  const aStr = a === 1 ? '' : `${a}`;
  const cStr = c === 1 ? '' : `${c}`;
  const expression = `(${aStr}${var1} ${sign1} ${b})(${cStr}${var2} ${sign2} ${d})`;

  const term1 = formatCoeffVar(a * c, sortVariablePart(var1 + var2));
  const term2 = formatCoeffVar(a * dSigned, var1);
  const term3 = formatCoeffVar(bSigned * c, var2);
  const term4 = formatCoeffVar(bSigned * dSigned, '');
  const rawTerms = [term1, term2, term3, term4].filter(t => t !== '0' && t !== '+0' && t !== '-0');
  const termsSorted = rawTerms.sort(compareTermsCanonical);
  const expected = termsSorted
    .map((t, i) => (i === 0 ? t : (t.startsWith('-') ? `- ${t.slice(1)}` : `+ ${t}`)))
    .join(' ');

  return {
    type: 'MultiplyBinomialsTask',
    data: { expression, expected },
  };
}

/* === RENDER TASK (polecenie) === */
export function renderTask({ data }) {
  return (
    <div className="cmd-container">
      <style>{`
        .cmd-container { margin-bottom: 14px; }    /* więcej przestrzeni nad "Pokaż odpowiedź" */
        .cmd-wide { display: inline; }
        .cmd-narrow { display: none; }
        @media (max-width: 880px) {
          .cmd-container { margin-bottom: 18px; }  /* na telefonie odsuń trochę bardziej */
          .cmd-wide { display: none; }
          .cmd-narrow { display: block; }
        }
      `}</style>

      {/* Desktop: wszystko w jednej linii */}
      <p className="cmd-wide">
        Uprość wyrażenie: <InlineMath math={data.expression} />
      </p>

      {/* Mobile: formuła w osobnym, wyśrodkowanym wierszu */}
      <div className="cmd-narrow">
        <p>Uprość wyrażenie:</p>
        <BlockMath math={data.expression} />
      </div>
    </div>
  );
}


/* === INPUT === */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <EquationInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      placeholder="np. 2x^2 + 3x + 1"
    />
  );
}

/* === CHECK ANSWER === */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'string') {
    return { isCorrect: false, expected: data.expected };
  }
  return {
    isCorrect: normalizeExpression(userAnswer) === normalizeExpression(data.expected),
    expected: data.expected,
  };
}

/* === ANSWER (Odpowiedź) === */
export function renderAnswer({ data }) {
  return (
    <>
      <style>{`
        .ans-wide { display: inline; }
        .ans-narrow { display: none; }
        @media (max-width: 880px) {
          .ans-wide { display: none; }
          .ans-narrow { display: block; }
        }
      `}</style>

      {/* Desktop: inline (nie wymusza nowego wiersza po "Odpowiedź:") */}
      <span className="ans-wide">
        <InlineMath math={data.expected} />
      </span>

      {/* Mobile: wyśrodkowany blok */}
      <span className="ans-narrow">
        <BlockMath math={data.expected} />
      </span>
    </>
  );
}

/* === USER ANSWER === */
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === 'string' ? userAnswer : userAnswer?.raw ?? '';
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={raw} />;
}

/* === EXPLANATION === */
export function renderExplanation({ data }) {
  const {expression,expected}=data;
  const match=expression.match(/^\((.*?) ([+-]) (\d+)\)\((.*?) ([+-]) (\d+)\)$/);
  if (!match) return <TaskSolutionSteps steps={[
    {title:"Mnożymy każdy składnik pierwszego nawiasu przez każdy składnik drugiego.",math:String.raw`(a+b)(c+d)=ac+ad+bc+bd`},
    {title:"Wykonujemy mnożenie i porządkujemy wyrazy.",math:`${expression}=${expected}`},
  ]}/>;
  const [,a,signB,b,c,signD,d]=match;
  const signedB=signB==="-"?`-${b}`:b, signedD=signD==="-"?`-${d}`:d;
  return <TaskSolutionSteps steps={[
    {title:"Każdy składnik pierwszego nawiasu mnożymy przez każdy składnik drugiego. Znaki należą do składników.",math:String.raw`(a+b)(c+d)=ac+ad+bc+bd`},
    {title:"Zapisujemy wszystkie cztery iloczyny, zachowując nawiasy wokół ujemnych składników.",math:String.raw`${expression}=(${a})\cdot(${c})+(${a})\cdot(${signedD})+(${signedB})\cdot(${c})+(${signedB})\cdot(${signedD})`},
    {title:"Mnożymy współczynniki i zmienne, a następnie porządkujemy wyrażenie.",math:`${expression}=${expected}`},
  ]}/>;
}
